import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useLocale } from "@seller/app/locale";
import { useSellerStoreSlug } from "@/lib/routing/useSellerStoreSlug";
import type { SellerProductReviewStatus } from "@/lib/api/types";
import { useSellerReviewsQuery } from "@/modules/reviews/services/useSellerReviewsQuery";
import { useUpdateReviewStatusMutation } from "@/modules/reviews/services/useUpdateReviewStatusMutation";
import { ReviewsSummaryCard } from "@/modules/reviews/components/ReviewsSummaryCard";
import { ReviewCard } from "@/modules/reviews/components/ReviewCard";
import {
  ReviewsFilterTabs,
  type ReviewFilterTab,
} from "@/modules/reviews/components/ReviewsFilterTabs";
import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { PageTransition } from "@/shared/components/PageTransition";
import { useIsMobile } from "@/shared/hooks/useIsMobile";
import { MobileHeaderNotificationBell } from "@/modules/notifications/components/MobileHeaderNotificationBell";

const PAGE_SIZE = 12;

type FilterId = SellerProductReviewStatus | "ALL";

export function ReviewsPage() {
  const locale = useLocale();
  const storeSlug = useSellerStoreSlug();
  const t = useTranslations();
  const isMobile = useIsMobile();
  const { setHero, setHeroAux } = usePageChrome();

  const [statusFilter, setStatusFilter] = useState<
    SellerProductReviewStatus | "ALL"
  >("ALL");
  const [offset, setOffset] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  // The page renders its own title/toolbar inline, so clear the shared chrome.
  useEffect(() => {
    setHero(null);
    setHeroAux(null);
    return () => {
      setHero(null);
      setHeroAux(null);
    };
  }, [setHero, setHeroAux]);

  const { data, isLoading, error } = useSellerReviewsQuery({
    storeSlug,
    status: statusFilter === "ALL" ? undefined : statusFilter,
    limit: PAGE_SIZE,
    offset,
  });
  // Count-only queries (limit 1 → read `total`) so the filter pills can show
  // per-status counts. Hidden is derived (all − published) to avoid a 3rd call.
  const { data: allCountData } = useSellerReviewsQuery({
    storeSlug,
    limit: 1,
    offset: 0,
  });
  const { data: publishedCountData } = useSellerReviewsQuery({
    storeSlug,
    status: "PUBLISHED",
    limit: 1,
    offset: 0,
  });
  const { mutateAsync: updateStatus, isPending } =
    useUpdateReviewStatusMutation();

  const allCount = allCountData?.total ?? 0;
  const publishedCount = publishedCountData?.total ?? 0;
  const hiddenCount = Math.max(0, allCount - publishedCount);

  const filterTabs: ReviewFilterTab<FilterId>[] = [
    { id: "ALL", label: t(keys.reviews.filterStatusAll), count: allCount },
    {
      id: "PUBLISHED",
      label: t(keys.reviews.filterStatusPublished),
      count: publishedCount,
      dot: "#16A34A",
    },
    {
      id: "HIDDEN",
      label: t(keys.reviews.filterStatusHidden),
      count: hiddenCount,
      dot: "var(--text-disabled)",
    },
  ];

  const changeFilter = (id: FilterId): void => {
    setStatusFilter(id);
    setOffset(0);
  };

  const handleToggle = async (
    id: string,
    nextStatus: SellerProductReviewStatus,
  ): Promise<void> => {
    setActionError(null);
    try {
      await updateStatus({ storeSlug, reviewId: id, status: nextStatus });
    } catch {
      setActionError(t(keys.reviews.actionError));
    }
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;
  const shownFrom = total === 0 ? 0 : offset + 1;
  const shownTo = Math.min(offset + items.length, total);

  const pageTitle = t(keys.reviews.pageTitle);
  const pageSubtitle = t(keys.reviews.pageSubtitle);

  const todayText = useMemo(
    () =>
      new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date()),
    [locale],
  );


  let content: ReactNode;
  if (isLoading && !data) {
    content = (
      <div className="text-sm text-[color:var(--text-muted)]">
        {t(keys.common.loading)}
      </div>
    );
  } else if (error) {
    content = (
      <div className="text-sm text-[color:var(--error-500)]">
        {t(keys.reviews.loadError)}
      </div>
    );
  } else if (items.length === 0) {
    content = (
      <div className="flex flex-col items-center gap-2 rounded-[18px] border border-[color:var(--border-light)] bg-[color:var(--surface)] py-14 text-center">
        <p className="text-[15px] font-semibold text-[color:var(--text-primary)]">
          {t(keys.reviews.emptyTitle)}
        </p>
        <p className="px-6 text-[13px] text-[color:var(--text-muted)]">
          {t(keys.reviews.emptyBody)}
        </p>
      </div>
    );
  } else {
    content = (
      <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fill,minmax(min(100%,320px),1fr))]">
        {items.map((review) => (
          <ReviewCard
            key={review.id}
            locale={locale}
            review={review}
            isMutating={isPending}
            onToggleStatus={handleToggle}
          />
        ))}
      </div>
    );
  }

  const showFooter = !isLoading && !error && items.length > 0;

  return (
    <>
      {/* Phone/tablet: compact sticky header */}
      {isMobile ? (
        <div
          className="sticky top-0 z-30 border-b border-[color:var(--hair)] bg-[color:var(--canvas)] px-4"
          style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 14px)" }}
        >
          {/* Title + notification pill */}
          <div className="flex items-center justify-between gap-2 px-1 pb-1 pt-1">
            <h1 className="text-[28px] font-extrabold tracking-[-0.025em] text-[color:var(--text-primary)]">
              {pageTitle}
            </h1>
            <div className="flex items-center gap-1 rounded-full bg-[color:var(--soft2)] p-[5px]">
              <MobileHeaderNotificationBell triggerClassName="relative flex h-[30px] w-[33px] items-center justify-center rounded-full text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]" />
            </div>
          </div>
          {/* Flat status tabs (Orders-style) */}
          <div className="flex items-center gap-1 overflow-x-auto px-1 pb-2 pt-1">
            {filterTabs.map((tab) => {
              const on = statusFilter === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeFilter(tab.id)}
                  className="whitespace-nowrap rounded-full px-3 py-[5px] text-[13px] font-semibold transition-colors"
                  style={{
                    background: on ? "var(--soft2)" : "transparent",
                    color: on ? "var(--text-primary)" : "var(--text-muted)",
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <PageTransition className="w-full">
        <div
          className={[
            "flex w-full flex-col gap-5",
            isMobile ? "px-4 pb-24 pt-4" : "px-7 py-6",
          ].join(" ")}
        >
          {/* Desktop title + date + export */}
          {!isMobile ? (
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <h1 className="text-[24px] font-bold leading-tight tracking-[-0.01em] text-[color:var(--text-primary)]">
                  {pageTitle}
                </h1>
                <p className="mt-1 text-[13.5px] text-[color:var(--text-muted)]">
                  {pageSubtitle}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2.5">
                <span className="inline-flex items-center gap-2 rounded-[11px] border border-[color:var(--border-light)] bg-[color:var(--card)] px-[13px] py-[9px] text-[13px] font-semibold text-[color:var(--text-secondary)]">
                  <CalendarIcon />
                  {todayText}
                </span>
              </div>
            </div>
          ) : null}

          {actionError ? (
            <div
              className="rounded-md border border-[color:var(--error-500)] bg-[color:var(--error-100)] px-4 py-3 text-sm text-[color:var(--error-500)]"
              role="alert"
            >
              {actionError}
            </div>
          ) : null}

          {!isMobile ? (
            <ReviewsSummaryCard
              locale={locale}
              items={items}
              total={allCount}
              publishedCount={publishedCount}
            />
          ) : null}

          <div className={isMobile ? "" : "rounded-[20px] border border-[color:var(--line2)] bg-[color:var(--card)] p-3 sm:p-4"}>
            {!isMobile ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <ReviewsFilterTabs
                  tabs={filterTabs}
                  value={statusFilter}
                  onChange={changeFilter}
                  ariaLabel={t(keys.reviews.filterStatus)}
                />
              </div>
            ) : null}

            {content}

            {showFooter ? (
              <>
                {/* Perforated divider */}
                <div
                  aria-hidden="true"
                  className="my-[18px] h-4"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(90deg,var(--border-strong) 0 1px,transparent 1px 9px)",
                    WebkitMaskImage:
                      "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
                    maskImage:
                      "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)",
                  }}
                />
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[13px] text-[color:var(--text-muted)]">
                    {t(keys.reviews.showingCount, {
                      shown: `${shownFrom}–${shownTo}`,
                      total,
                    })}
                  </span>
                  {totalPages > 1 ? (
                    <div className="flex items-center gap-1">
                      <PagerButton
                        ariaLabel={t(keys.reviews.pagerPrev)}
                        disabled={offset === 0}
                        onClick={() =>
                          setOffset((prev) => Math.max(0, prev - PAGE_SIZE))
                        }
                      >
                        <ChevronLeftIcon />
                      </PagerButton>
                      {pageWindow(currentPage, totalPages).map((item, index) =>
                        typeof item === "number" ? (
                          <button
                            key={item}
                            type="button"
                            onClick={() => setOffset((item - 1) * PAGE_SIZE)}
                            aria-current={
                              item === currentPage ? "page" : undefined
                            }
                            className={[
                              "h-[34px] min-w-[34px] rounded-[9px] px-2 text-[13px] font-semibold transition-colors",
                              item === currentPage
                                ? "bg-[color:var(--accent-500)] text-[color:var(--on-accent)]"
                                : "border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] hover:bg-[color:var(--hover)]",
                            ].join(" ")}
                          >
                            {item}
                          </button>
                        ) : (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-1 text-[13px] text-[color:var(--text-muted)]"
                          >
                            …
                          </span>
                        ),
                      )}
                      <PagerButton
                        ariaLabel={t(keys.reviews.pagerNext)}
                        disabled={offset + PAGE_SIZE >= total}
                        onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                      >
                        <ChevronRightIcon />
                      </PagerButton>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </PageTransition>

    </>
  );
}

function PagerButton({
  children,
  onClick,
  disabled,
  ariaLabel,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled: boolean;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className="flex h-[34px] w-[34px] items-center justify-center rounded-[9px] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--hover)] disabled:cursor-not-allowed disabled:opacity-40"
    >
      {children}
    </button>
  );
}

/**
 * Page numbers to render: all when ≤7, otherwise a windowed set (first, last,
 * current ±1) with "…" gaps.
 */
function pageWindow(current: number, total: number): (number | "…")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  const wanted = [1, total, current, current - 1, current + 1]
    .filter((page) => page >= 1 && page <= total)
    .sort((a, b) => a - b);
  const unique = [...new Set(wanted)];
  const out: (number | "…")[] = [];
  let prev = 0;
  for (const page of unique) {
    if (page - prev > 1) out.push("…");
    out.push(page);
    prev = page;
  }
  return out;
}

function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="18" rx="2.5" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

function ChevronLeftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m15 6-6 6 6 6" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m9 6 6 6-6 6" />
    </svg>
  );
}
